import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Typography,
  Button,
  alpha,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Menu,
  Stack,
  InputAdornment,
  IconButton,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from "@mui/material";
import {
  AccountBalance as BillDeskIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  ReceiptLong as BillIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Payment as PaymentIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon,
  CheckCircle as CompletedIcon,
  PendingActions as PendingIcon,
  CalendarMonth as CalendarIcon,
  KeyboardArrowDown as ChevronDownIcon,
  Payments as ReceivedIcon,
  ShoppingCart as OrdersIcon,
} from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import PageContentLayout from "../components/layout/PageContentLayout";
import apiService from "../services/apiService";
import MdlAddNewBill from "../components/dialog/MdlAddNewBill";
import { crmColors } from "../theme/colors";

const formatMoney = (value, currency = "INR") => {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  const prefix = currency === "INR" || !currency ? "₹" : `${currency} `;
  return `${prefix}${n.toLocaleString("en-IN")}`;
};

const fieldSx = { borderRadius: "10px", bgcolor: "#fff" };

const SectionPanel = ({ children, tone = "default" }) => {
  const border =
    tone === "success"
      ? alpha(crmColors.success.main, 0.22)
      : tone === "energy"
        ? alpha(crmColors.energy.main, 0.22)
        : crmColors.border.default;
  const bg =
    tone === "success"
      ? `linear-gradient(160deg, ${alpha(crmColors.success.main, 0.07)} 0%, ${alpha("#fff", 0.95)} 55%)`
      : tone === "energy"
        ? `linear-gradient(160deg, ${alpha(crmColors.energy.main, 0.07)} 0%, ${alpha("#fff", 0.95)} 55%)`
        : `linear-gradient(180deg, ${alpha(crmColors.primary.main, 0.03)} 0%, ${crmColors.background.paper} 100%)`;
  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 1.75 },
        borderRadius: "14px",
        border: `1px solid ${border}`,
        background: bg,
        boxShadow: `0 1px 0 ${alpha("#fff", 0.8)} inset`,
      }}
    >
      {children}
    </Box>
  );
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString("en-IN");
};

const getOrderPaidAmount = (order) => {
  if (!order) return 0;
  if (order.amount_paid != null && order.amount_paid !== "") {
    return Number(order.amount_paid) || 0;
  }
  if (Array.isArray(order.payments) && order.payments.length) {
    return order.payments.reduce((sum, p) => sum + Number(p.transaction_amount || 0), 0);
  }
  return 0;
};

const orderDueAmount = (order) =>
  Math.max(0, Number(order?.total_amount || 0) - getOrderPaidAmount(order));

const orderPaymentPending = (order) => orderDueAmount(order) > 0.009;

const paymentLabel = (order) => {
  const paid = getOrderPaidAmount(order);
  const total = Number(order?.total_amount || 0);
  if (total > 0 && paid >= total - 0.009) return "Paid";
  if (paid > 0) return "Partial";
  return "Pending";
};

const MONTH_OPTIONS = [
  { value: "all", label: "All months" },
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const BillDesk = () => {
  const now = new Date();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [payMethods, setPayMethods] = useState([]);
  const [catalogReady, setCatalogReady] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [financialYears, setFinancialYears] = useState([]);
  const [fyLoading, setFyLoading] = useState(true);
  const [selectedFyId, setSelectedFyId] = useState("");
  const [monthFilter, setMonthFilter] = useState(String(now.getMonth() + 1));
  const [fyAnchor, setFyAnchor] = useState(null);
  const [monthAnchor, setMonthAnchor] = useState(null);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({
    transaction_amount: "",
    payment_method: "Online",
    payment_date: todayISO(),
    payment_remark: "",
    installment_id: "",
    payment_channel: "Manual",
    gateway_transaction_id: "",
  });
  const [saving, setSaving] = useState(false);
  const [billOpen, setBillOpen] = useState(false);
  const [billSeed, setBillSeed] = useState({ school: "", product: "" });

  const fetchOrders = useCallback(async () => {
    setOrdersLoading(true);
    const result = await apiService.getOrderList({ limit: 200, offset: 0, order: "DESC" });
    if (result.success) {
      setOrders(Array.isArray(result.data) ? result.data : []);
    } else {
      toast.error(result.error || "Failed to load orders");
      setOrders([]);
    }
    setOrdersLoading(false);
  }, []);

  const fetchCatalog = useCallback(async () => {
    const [productsRes, taxRes, payRes] = await Promise.all([
      apiService.getProductRateList(),
      apiService.getTaxList(),
      apiService.getPaymentMethodList(),
    ]);
    if (productsRes.success) {
      setRows(Array.isArray(productsRes.data) ? productsRes.data : []);
    } else {
      setRows([]);
    }
    if (taxRes.success) {
      setTaxes(Array.isArray(taxRes.data) ? taxRes.data : []);
    }
    if (payRes.success) {
      setPayMethods(Array.isArray(payRes.data) ? payRes.data : []);
    }
    setCatalogReady(true);
  }, []);

  //Created By Cursor — financial years from getFinancialYearList
  const loadFinancialYears = useCallback(async () => {
    setFyLoading(true);
    const result = await apiService.getFinancialYearList();
    if (result.success) {
      const list = Array.isArray(result.data) ? result.data : [];
      setFinancialYears(list);
      const current =
        list.find((fy) => {
          const status = String(fy.status || fy.financial_year_status || "");
          return status.toLowerCase() === "current";
        }) || list[0];
      if (current) {
        const id = String(
          current.financial_year_id || current.year_id || current.id || ""
        );
        setSelectedFyId((prev) => prev || id);
      }
    } else {
      setFinancialYears([]);
      toast.error(result.error || "Failed to load financial years");
    }
    setFyLoading(false);
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchCatalog();
    loadFinancialYears();
  }, [fetchOrders, fetchCatalog, loadFinancialYears]);

  const statusOptions = useMemo(
    () => [
      "all",
      ...Array.from(new Set(orders.map((o) => o.order_status_name).filter(Boolean))),
    ],
    [orders]
  );

  const selectedFy = useMemo(() => {
    if (!selectedFyId || selectedFyId === "all") return null;
    return (
      financialYears.find((fy) => {
        const id = String(fy.financial_year_id || fy.year_id || fy.id || "");
        return id === String(selectedFyId);
      }) || null
    );
  }, [financialYears, selectedFyId]);

  const selectedFyLabel = useMemo(() => {
    if (fyLoading) return "Loading…";
    if (!selectedFyId || selectedFyId === "all") return "All years";
    return (
      selectedFy?.financial_year_name ||
      selectedFy?.name ||
      selectedFy?.label ||
      String(selectedFyId)
    );
  }, [fyLoading, selectedFyId, selectedFy]);

  const selectedMonthLabel = useMemo(() => {
    return (
      MONTH_OPTIONS.find((m) => m.value === monthFilter)?.label || "All months"
    );
  }, [monthFilter]);

  const filterFieldSx = {
    height: 44,
    px: 1.25,
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: 700,
    fontSize: 13,
    borderColor: crmColors.border.primaryTint,
    color: crmColors.text.primary,
    bgcolor: alpha(crmColors.primary.main, 0.04),
    "& .MuiButton-startIcon": { mr: 1 },
    "&:hover": {
      borderColor: crmColors.primary.main,
      bgcolor: alpha(crmColors.primary.main, 0.08),
    },
  };

  const filteredOrders = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const monthNum = monthFilter === "all" ? null : Number(monthFilter);
    let fyStart = null;
    let fyEnd = null;
    if (selectedFy?.start_date) {
      fyStart = new Date(selectedFy.start_date);
      if (Number.isNaN(fyStart.getTime())) fyStart = null;
    }
    if (selectedFy?.end_date) {
      fyEnd = new Date(selectedFy.end_date);
      if (!Number.isNaN(fyEnd.getTime())) {
        fyEnd.setHours(23, 59, 59, 999);
      } else {
        fyEnd = null;
      }
    }

    return orders.filter((order) => {
      const matchesSearch =
        (order.order_id || "").toLowerCase().includes(term) ||
        (order.school_name || "").toLowerCase().includes(term) ||
        (order.invoice_id || "").toLowerCase().includes(term) ||
        (order.school_code || "").toLowerCase().includes(term);
      const matchesStatus =
        statusFilter === "all" || order.order_status_name === statusFilter;
      let matchesPeriod = true;
      if (fyStart || fyEnd || monthNum != null) {
        const d = order.order_date ? new Date(order.order_date) : null;
        if (!d || Number.isNaN(d.getTime())) {
          matchesPeriod = false;
        } else {
          if (fyStart && d < fyStart) matchesPeriod = false;
          if (fyEnd && d > fyEnd) matchesPeriod = false;
          if (monthNum != null && d.getMonth() + 1 !== monthNum) matchesPeriod = false;
        }
      }
      return matchesSearch && matchesStatus && matchesPeriod;
    });
  }, [orders, searchTerm, statusFilter, selectedFy, monthFilter]);

  const totalRevenue = useMemo(
    () => filteredOrders.reduce((sum, o) => sum + Number(o.amount_paid || 0), 0),
    [filteredOrders]
  );
  const pendingOrders = useMemo(
    () =>
      filteredOrders.filter((o) =>
        (o.order_status_name || "").toLowerCase().includes("pending")
      ).length,
    [filteredOrders]
  );
  const confirmedOrders = useMemo(
    () =>
      filteredOrders.filter((o) =>
        (o.order_status_name || "").toLowerCase().includes("confirm")
      ).length,
    [filteredOrders]
  );

  const getStatusColor = (status) => {
    const s = (status || "").toLowerCase();
    if (s.includes("confirm")) return "success";
    if (s.includes("pending")) return "warning";
    return "default";
  };

  const handleViewOrder = async (order) => {
    setOrderDialogOpen(true);
    setSelectedOrder(order);
    setDetailLoading(true);
    const result = await apiService.getOrderDetail(order.order_id);
    if (result.success) {
      setSelectedOrder(result.data);
    } else {
      toast.error(result.error || "Failed to load order details");
    }
    setDetailLoading(false);
  };

  const openPayDialog = (order) => {
    if (!orderPaymentPending(order)) {
      toast.error("This order is already fully paid");
      return;
    }
    const due = orderDueAmount(order);
    setSelectedOrder(order);
    setPayForm({
      transaction_amount: due > 0 ? String(due) : "",
      payment_method: payMethods[0]?.payment_mode_id
        ? String(payMethods[0].payment_mode_id)
        : "Online",
      payment_date: todayISO(),
      payment_remark: "",
      installment_id: "",
      payment_channel: "Manual",
      gateway_transaction_id: "",
    });
    setPayOpen(true);
  };

  const handleRecordPayment = async () => {
    if (!selectedOrder?.order_id) {
      toast.error("No order selected");
      return;
    }
    if (!orderPaymentPending(selectedOrder)) {
      toast.error("This order is already fully paid");
      setPayOpen(false);
      return;
    }
    if (!payForm.transaction_amount || Number(payForm.transaction_amount) <= 0) {
      toast.error("Enter a valid payment amount");
      return;
    }
    if (
      payForm.payment_channel === "Payment Gateway" &&
      !payForm.gateway_transaction_id?.trim()
    ) {
      toast.error("Gateway transaction ID is required for Payment Gateway");
      return;
    }
    setSaving(true);
    const result = await apiService.recordOrderPayment({
      order_id: selectedOrder.order_id,
      school_code: selectedOrder.school_code,
      transaction_amount: payForm.transaction_amount,
      payment_method: payForm.payment_method,
      payment_date: payForm.payment_date,
      payment_remark: payForm.payment_remark,
      installment_id: payForm.installment_id,
      payment_channel: payForm.payment_channel || "Manual",
      gateway_transaction_id: payForm.gateway_transaction_id || "",
    });
    setSaving(false);
    if (result.success) {
      const sub = result.data?.subscription;
      if (sub?.error) {
        toast.error(`Payment saved, but subscription not updated: ${sub.error}`);
      } else {
        toast.success(
          `Payment recorded${result.data?.invoice_id ? ` · ${result.data.invoice_id}` : ""}${
            sub?.subscription_id ? ` · ${sub.subscription_id}` : ""
          }`
        );
      }
      setPayOpen(false);
      fetchOrders();
      if (orderDialogOpen && selectedOrder?.order_id) {
        handleViewOrder({ order_id: selectedOrder.order_id });
      }
    } else {
      toast.error(result.error || "Failed to record payment");
    }
  };

  //Created By Cursor — open Add New Bill dialog (MdlAddNewBill)
  const openNewBill = (productCode = "", schoolCode = "") => {
    setBillSeed({
      school: schoolCode || "",
      product: productCode || "",
    });
    setBillOpen(true);
  };

  //Created By Cursor — open bill from Product List / Renewals (?product=&school=)
  useEffect(() => {
    if (!catalogReady) return;
    const product = (searchParams.get("product") || "").trim();
    const school = (searchParams.get("school") || "").trim();
    if (!product && !school) return;
    openNewBill(product, school);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open once when catalog ready
  }, [catalogReady, searchParams, setSearchParams]);


  const actionBtnSx = {
    textTransform: "none",
    borderRadius: "8px",
    fontWeight: 600,
    borderColor: alpha("#fff", 0.45),
    color: "#fff",
    "&:hover": { borderColor: "#fff", bgcolor: alpha("#fff", 0.1) },
  };

  return (
    <PageContentLayout
      title="BillDesk"
      subtitle="Create bills and record payments"
      barSubtitle={`${filteredOrders.length} order${filteredOrders.length === 1 ? "" : "s"}`}
      breadcrumbs={[
        { label: "BillDesk", icon: <BillDeskIcon sx={{ color: "#ffffff", fontSize: 18 }} /> },
      ]}
      backTo="/dashboard"
      darkBar
      actions={
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => openNewBill()}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: 700,
              bgcolor: crmColors.energy.main,
              color: "#fff",
              "&:hover": { bgcolor: crmColors.energy.dark },
            }}
          >
            Add new bill
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={fetchOrders}
            disabled={ordersLoading}
            sx={actionBtnSx}
          >
            Refresh
          </Button>
        </Stack>
      }
    >
        <Box>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {[
              {
                label: "Total orders",
                value: filteredOrders.length,
                color: crmColors.primary.main,
                icon: <OrdersIcon sx={{ fontSize: 18 }} />,
              },
              {
                label: "Received",
                value: `₹${totalRevenue.toLocaleString("en-IN")}`,
                color: crmColors.success.main,
                icon: <ReceivedIcon sx={{ fontSize: 18 }} />,
              },
              {
                label: "Pending",
                value: pendingOrders,
                color: crmColors.warning.main,
                icon: <PendingIcon sx={{ fontSize: 18 }} />,
              },
              {
                label: "Confirmed",
                value: confirmedOrders,
                color: crmColors.success.dark,
                icon: <CompletedIcon sx={{ fontSize: 18 }} />,
              },
            ].map((m) => (
              <Grid item xs={6} sm={3} key={m.label}>
                <Box
                  sx={{
                    overflow: "hidden",
                    px: 1.6,
                    py: 1.35,
                    borderRadius: "14px",
                    border: `1px solid ${alpha(m.color, 0.18)}`,
                    background: `linear-gradient(145deg, ${crmColors.background.paper} 0%, ${alpha(m.color, 0.07)} 100%)`,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.1, mb: 0.55 }}>
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: "9px",
                        display: "grid",
                        placeItems: "center",
                        bgcolor: alpha(m.color, 0.14),
                        color: m.color,
                      }}
                    >
                      {m.icon}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 800,
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                        color: crmColors.text.secondary,
                      }}
                    >
                      {m.label}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 21,
                      fontWeight: 800,
                      color: m.color,
                      lineHeight: 1.15,
                      letterSpacing: "-0.02em",
                      pl: 0.25,
                    }}
                  >
                    {m.value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box
            sx={{
              display: "flex",
              gap: 1.15,
              flexWrap: "wrap",
              alignItems: "center",
              mb: 1.75,
              p: 1.25,
              borderRadius: "16px",
              border: `1px solid ${crmColors.border.default}`,
              bgcolor: alpha(crmColors.primary.main, 0.025),
              background: `linear-gradient(120deg, ${crmColors.background.paper} 0%, ${alpha(crmColors.primary.main, 0.04)} 100%)`,
            }}
          >
            <Button
              variant="outlined"
              size="small"
              disableElevation
              disabled={fyLoading}
              endIcon={<ChevronDownIcon sx={{ fontSize: 18 }} />}
              startIcon={
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "8px",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: alpha(crmColors.primary.main, 0.12),
                    color: crmColors.primary.main,
                  }}
                >
                  <CalendarIcon sx={{ fontSize: 15 }} />
                </Box>
              }
              onClick={(e) => setFyAnchor(e.currentTarget)}
              sx={filterFieldSx}
            >
              <Box sx={{ textAlign: "left", lineHeight: 1.15, pr: 0.5 }}>
                <Typography
                  component="span"
                  sx={{
                    display: "block",
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: crmColors.text.secondary,
                  }}
                >
                  Financial year
                </Typography>
                <Typography component="span" sx={{ fontSize: 13, fontWeight: 800 }}>
                  {selectedFyLabel}
                </Typography>
              </Box>
            </Button>
            <Menu
              anchorEl={fyAnchor}
              open={Boolean(fyAnchor)}
              onClose={() => setFyAnchor(null)}
              PaperProps={{
                sx: {
                  borderRadius: 2.5,
                  mt: 1,
                  minWidth: 240,
                  border: `1px solid ${crmColors.border.default}`,
                  boxShadow: "0 12px 28px -12px rgba(15,31,47,0.28)",
                },
              }}
            >
              <MenuItem
                selected={selectedFyId === "all" || !selectedFyId}
                onClick={() => {
                  setSelectedFyId("all");
                  setFyAnchor(null);
                }}
                sx={{ borderRadius: 1.5, mx: 0.75, my: 0.25, fontWeight: 700 }}
              >
                All years
              </MenuItem>
              {financialYears.map((fy) => {
                const id = String(fy.financial_year_id || fy.year_id || fy.id || "");
                const label = fy.financial_year_name || fy.name || fy.label || id;
                const isCurrent = String(fy.status || "").toLowerCase() === "current";
                const selected = String(id) === String(selectedFyId);
                return (
                  <MenuItem
                    key={id}
                    selected={selected}
                    onClick={() => {
                      setSelectedFyId(id);
                      setFyAnchor(null);
                    }}
                    sx={{
                      borderRadius: 1.5,
                      mx: 0.75,
                      my: 0.25,
                      fontWeight: selected ? 800 : 600,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                      <Box sx={{ flex: 1 }}>{label}</Box>
                      {isCurrent ? (
                        <Chip
                          size="small"
                          label="Current"
                          sx={{
                            height: 20,
                            fontSize: 10,
                            fontWeight: 800,
                            bgcolor: alpha(crmColors.primary.main, 0.12),
                            color: crmColors.primary.main,
                          }}
                        />
                      ) : null}
                    </Box>
                  </MenuItem>
                );
              })}
            </Menu>

            <Button
              variant="outlined"
              size="small"
              disableElevation
              endIcon={<ChevronDownIcon sx={{ fontSize: 18 }} />}
              startIcon={
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "8px",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: alpha(crmColors.energy.main, 0.14),
                    color: crmColors.energy.dark,
                  }}
                >
                  <CalendarIcon sx={{ fontSize: 15 }} />
                </Box>
              }
              onClick={(e) => setMonthAnchor(e.currentTarget)}
              sx={{
                ...filterFieldSx,
                borderColor: alpha(crmColors.energy.main, 0.28),
                bgcolor: alpha(crmColors.energy.main, 0.04),
                "&:hover": {
                  borderColor: crmColors.energy.main,
                  bgcolor: alpha(crmColors.energy.main, 0.08),
                },
              }}
            >
              <Box sx={{ textAlign: "left", lineHeight: 1.15, pr: 0.5 }}>
                <Typography
                  component="span"
                  sx={{
                    display: "block",
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: crmColors.text.secondary,
                  }}
                >
                  Month
                </Typography>
                <Typography component="span" sx={{ fontSize: 13, fontWeight: 800 }}>
                  {selectedMonthLabel}
                </Typography>
              </Box>
            </Button>
            <Menu
              anchorEl={monthAnchor}
              open={Boolean(monthAnchor)}
              onClose={() => setMonthAnchor(null)}
              PaperProps={{
                sx: {
                  borderRadius: 2.5,
                  mt: 1,
                  minWidth: 180,
                  maxHeight: 320,
                  border: `1px solid ${crmColors.border.default}`,
                  boxShadow: "0 12px 28px -12px rgba(15,31,47,0.28)",
                },
              }}
            >
              {MONTH_OPTIONS.map((m) => (
                <MenuItem
                  key={m.value}
                  selected={monthFilter === m.value}
                  onClick={() => {
                    setMonthFilter(m.value);
                    setMonthAnchor(null);
                  }}
                  sx={{
                    borderRadius: 1.5,
                    mx: 0.75,
                    my: 0.25,
                    fontWeight: monthFilter === m.value ? 800 : 600,
                  }}
                >
                  {m.label}
                </MenuItem>
              ))}
            </Menu>

            <TextField
              placeholder="Search order, invoice, school…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{
                flexGrow: 1,
                minWidth: 200,
                "& .MuiOutlinedInput-root": {
                  height: 44,
                  borderRadius: "12px",
                  bgcolor: crmColors.background.paper,
                  "& fieldset": { borderColor: crmColors.border.default },
                  "&:hover fieldset": { borderColor: crmColors.border.primaryTint },
                  "&.Mui-focused fieldset": { borderColor: crmColors.primary.main },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: crmColors.text.secondary }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant={statusFilter !== "all" ? "contained" : "outlined"}
              startIcon={<FilterIcon />}
              onClick={(e) => setFilterAnchor(e.currentTarget)}
              size="small"
              sx={{
                height: 44,
                px: 1.5,
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "12px",
                ...(statusFilter === "all"
                  ? {
                      borderColor: crmColors.border.primaryTint,
                      color: crmColors.text.primary,
                      bgcolor: crmColors.background.paper,
                    }
                  : {
                      bgcolor: crmColors.primary.main,
                      "&:hover": { bgcolor: crmColors.primary.dark },
                    }),
              }}
            >
              {statusFilter === "all" ? "All status" : statusFilter}
            </Button>
            <Menu
              anchorEl={filterAnchor}
              open={Boolean(filterAnchor)}
              onClose={() => setFilterAnchor(null)}
              PaperProps={{
                sx: {
                  borderRadius: 2.5,
                  mt: 1,
                  minWidth: 160,
                  border: `1px solid ${crmColors.border.default}`,
                  boxShadow: "0 12px 28px -12px rgba(15,31,47,0.28)",
                },
              }}
            >
              {statusOptions.map((opt) => (
                <MenuItem
                  key={opt}
                  selected={statusFilter === opt}
                  onClick={() => {
                    setStatusFilter(opt);
                    setFilterAnchor(null);
                  }}
                  sx={{
                    borderRadius: 1.5,
                    mx: 0.75,
                    my: 0.25,
                    fontWeight: statusFilter === opt ? 800 : 600,
                  }}
                >
                  {opt === "all" ? "All status" : opt}
                </MenuItem>
              ))}
            </Menu>
          </Box>

          <Card
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: `1px solid ${crmColors.border.default}`,
              overflow: "hidden",
              boxShadow: `0 10px 28px ${alpha(crmColors.primary.main, 0.05)}`,
            }}
          >
            {ordersLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 7 }}>
                <CircularProgress size={28} sx={{ color: crmColors.primary.main }} />
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: alpha(crmColors.primary.main, 0.045),
                        "& .MuiTableCell-root": {
                          fontWeight: 800,
                          fontSize: 11,
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          color: crmColors.text.secondary,
                          borderBottom: `1px solid ${crmColors.border.default}`,
                          py: 1.15,
                        },
                      }}
                    >
                      <TableCell>Order</TableCell>
                      <TableCell>School</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Paid</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Payment</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredOrders.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              mx: "auto",
                              mb: 1.25,
                              borderRadius: "14px",
                              display: "grid",
                              placeItems: "center",
                              bgcolor: alpha(crmColors.primary.main, 0.1),
                              color: crmColors.primary.main,
                            }}
                          >
                            <BillIcon sx={{ fontSize: 24 }} />
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: crmColors.text.primary }}
                          >
                            No orders in this period
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mb: 1.5 }}
                          >
                            Try another month or create a new bill
                          </Typography>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => openNewBill()}
                            sx={{
                              textTransform: "none",
                              fontWeight: 700,
                              borderRadius: "10px",
                              bgcolor: crmColors.energy.main,
                              "&:hover": { bgcolor: crmColors.energy.dark },
                            }}
                          >
                            Add new bill
                          </Button>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredOrders.map((order) => {
                        const payState = paymentLabel(order);
                        return (
                          <TableRow
                            key={order.order_id}
                            hover
                            sx={{
                              "&:hover": { bgcolor: alpha(crmColors.primary.main, 0.03) },
                              "& .MuiTableCell-root": {
                                borderBottom: `1px solid ${alpha(crmColors.border.default, 0.85)}`,
                                py: 1.2,
                              },
                            }}
                          >
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 800,
                                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                  fontSize: 12.5,
                                  color: crmColors.primary.main,
                                }}
                              >
                                {order.order_id}
                              </Typography>
                              {order.invoice_id ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {order.invoice_id}
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {order.school_name || "—"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {order.school_code || ""}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {order.product_name || order.product_code || "—"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {formatDate(order.order_date)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography sx={{ fontWeight: 800, fontSize: 13 }}>
                                ₹{Number(order.total_amount || 0).toLocaleString("en-IN")}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                sx={{
                                  fontWeight: 800,
                                  fontSize: 13,
                                  color:
                                    Number(order.amount_paid || 0) > 0
                                      ? crmColors.success.main
                                      : crmColors.text.secondary,
                                }}
                              >
                                ₹{Number(order.amount_paid || 0).toLocaleString("en-IN")}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={
                                  (order.order_status_name || "")
                                    .toLowerCase()
                                    .includes("confirm") ? (
                                    <CompletedIcon sx={{ fontSize: "16px !important" }} />
                                  ) : (
                                    <PendingIcon sx={{ fontSize: "16px !important" }} />
                                  )
                                }
                                label={order.order_status_name || "—"}
                                size="small"
                                color={getStatusColor(order.order_status_name)}
                                sx={{ fontWeight: 700, borderRadius: "8px" }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={payState}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontWeight: 700,
                                  borderRadius: "8px",
                                  borderColor:
                                    payState === "Paid"
                                      ? alpha(crmColors.success.main, 0.45)
                                      : payState === "Partial"
                                        ? alpha(crmColors.warning.main, 0.45)
                                        : alpha(crmColors.text.secondary, 0.35),
                                  color:
                                    payState === "Paid"
                                      ? crmColors.success.dark
                                      : payState === "Partial"
                                        ? crmColors.warning.dark
                                        : crmColors.text.secondary,
                                  bgcolor:
                                    payState === "Paid"
                                      ? alpha(crmColors.success.main, 0.08)
                                      : payState === "Partial"
                                        ? alpha(crmColors.warning.main, 0.08)
                                        : alpha(crmColors.text.secondary, 0.06),
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewOrder(order)}
                                  title="View"
                                  sx={{
                                    bgcolor: alpha(crmColors.primary.main, 0.08),
                                    color: crmColors.primary.main,
                                    "&:hover": { bgcolor: alpha(crmColors.primary.main, 0.16) },
                                  }}
                                >
                                  <ViewIcon fontSize="small" />
                                </IconButton>
                                {payState !== "Paid" ? (
                                  <IconButton
                                    size="small"
                                    onClick={() => openPayDialog(order)}
                                    title="Record payment"
                                    sx={{
                                      bgcolor: alpha(crmColors.success.main, 0.1),
                                      color: crmColors.success.dark,
                                      "&:hover": { bgcolor: alpha(crmColors.success.main, 0.18) },
                                    }}
                                  >
                                    <PaymentIcon fontSize="small" />
                                  </IconButton>
                                ) : null}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        </Box>

      <Dialog
        open={orderDialogOpen}
        onClose={() => setOrderDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            border: `1px solid ${crmColors.border.default}`,
          },
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            background: `linear-gradient(125deg, ${crmColors.primary.darker} 0%, ${crmColors.hero.start} 48%, ${crmColors.hero.mid} 100%)`,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            position: "relative",
            overflow: "hidden",
            "&::after": {
              content: '""',
              position: "absolute",
              right: -40,
              top: -50,
              width: 160,
              height: 160,
              borderRadius: "50%",
              bgcolor: alpha("#fff", 0.06),
              pointerEvents: "none",
            },
          }}
        >
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", position: "relative", zIndex: 1 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                display: "grid",
                placeItems: "center",
                bgcolor: alpha("#fff", 0.16),
                border: `1px solid ${alpha("#fff", 0.22)}`,
              }}
            >
              <BillIcon />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: "1.12rem", lineHeight: 1.2 }}>
                {selectedOrder?.order_id || "Order details"}
              </Typography>
              <Typography sx={{ fontSize: 12, opacity: 0.88, mt: 0.2 }}>
                {selectedOrder?.invoice_id
                  ? `${selectedOrder.invoice_id} · `
                  : ""}
                {selectedOrder?.school_name || "Order summary"}
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
            {selectedOrder ? (
              <Chip
                size="small"
                label={paymentLabel(selectedOrder)}
                sx={{
                  fontWeight: 800,
                  bgcolor: alpha("#fff", 0.18),
                  color: "#fff",
                  border: `1px solid ${alpha("#fff", 0.28)}`,
                }}
              />
            ) : null}
            <IconButton
              size="small"
              onClick={() => setOrderDialogOpen(false)}
              sx={{
                color: "#fff",
                bgcolor: alpha("#fff", 0.12),
                border: `1px solid ${alpha("#fff", 0.18)}`,
                "&:hover": { bgcolor: alpha("#fff", 0.22) },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        <DialogContent
          sx={{
            px: { xs: 1.75, sm: 2.25 },
            py: 2,
            bgcolor: alpha(crmColors.primary.main, 0.02),
          }}
        >
          {detailLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress size={28} sx={{ color: crmColors.primary.main }} />
            </Box>
          ) : selectedOrder ? (
            <Stack spacing={1.75}>
              <Grid container spacing={1.25}>
                {[
                  {
                    label: "Total",
                    value: `₹${Number(selectedOrder.total_amount || 0).toLocaleString("en-IN")}`,
                    color: crmColors.primary.main,
                  },
                  {
                    label: "Paid",
                    value: `₹${getOrderPaidAmount(selectedOrder).toLocaleString("en-IN")}`,
                    color: crmColors.success.main,
                  },
                  {
                    label: "Due",
                    value: `₹${orderDueAmount(selectedOrder).toLocaleString("en-IN")}`,
                    color: orderPaymentPending(selectedOrder)
                      ? crmColors.warning.main
                      : crmColors.success.dark,
                  },
                ].map((m) => (
                  <Grid item xs={4} key={m.label}>
                    <Box
                      sx={{
                        px: 1.4,
                        py: 1.2,
                        borderRadius: "12px",
                        border: `1px solid ${alpha(m.color, 0.2)}`,
                        bgcolor: alpha(m.color, 0.06),
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontWeight: 800,
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                          color: crmColors.text.secondary,
                        }}
                      >
                        {m.label}
                      </Typography>
                      <Typography sx={{ fontSize: 18, fontWeight: 800, color: m.color, mt: 0.2 }}>
                        {m.value}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <SectionPanel>
                <Grid container spacing={1.75}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      School
                    </Typography>
                    <Typography sx={{ fontWeight: 700 }}>
                      {selectedOrder.school_name || "—"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedOrder.school_code || ""}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Product
                    </Typography>
                    <Typography sx={{ fontWeight: 700 }}>
                      {selectedOrder.product_name || selectedOrder.product_code || "—"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedOrder.order_type || ""} · {selectedOrder.order_status_name || selectedOrder.order_status || ""}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Order date
                    </Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                      {formatDate(selectedOrder.order_date)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Service period
                    </Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                      {formatDate(selectedOrder.service_start_date)} →{" "}
                      {formatDate(selectedOrder.service_end_date)}
                    </Typography>
                    {selectedOrder.service_validity ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {selectedOrder.service_validity}
                      </Typography>
                    ) : null}
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Subtotal
                    </Typography>
                    <Typography sx={{ fontWeight: 700 }}>
                      ₹{Number(selectedOrder.product_order_amount || 0).toLocaleString("en-IN")}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Tax
                    </Typography>
                    <Typography sx={{ fontWeight: 700 }}>
                      ₹{Number(selectedOrder.tax_amount || 0).toLocaleString("en-IN")}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Discount
                    </Typography>
                    <Typography sx={{ fontWeight: 700 }}>
                      ₹{Number(selectedOrder.promo_code_discount || 0).toLocaleString("en-IN")}
                    </Typography>
                  </Grid>
                </Grid>
              </SectionPanel>

              {selectedOrder.subscription ? (
                <SectionPanel tone="success">
                  <Typography sx={{ fontWeight: 800, mb: 0.5, fontSize: 13 }}>
                    Subscription · {selectedOrder.subscription.subscription_id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {selectedOrder.subscription.status} · ends{" "}
                    {formatDate(selectedOrder.subscription.service_end_date)}
                    {selectedOrder.subscription.billing_cycle_months
                      ? ` · ${selectedOrder.subscription.billing_cycle_months} mo cycle`
                      : ""}
                  </Typography>
                </SectionPanel>
              ) : null}

              {Array.isArray(selectedOrder.installments) &&
              selectedOrder.installments.length > 0 ? (
                <SectionPanel>
                  <Typography sx={{ fontWeight: 800, mb: 1, fontSize: 13 }}>
                    Installments
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow
                          sx={{
                            "& .MuiTableCell-root": {
                              fontWeight: 800,
                              fontSize: 11,
                              color: crmColors.text.secondary,
                            },
                          }}
                        >
                          <TableCell>#</TableCell>
                          <TableCell>Due</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Due</TableCell>
                          <TableCell align="right">Paid</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedOrder.installments.map((ins) => (
                          <TableRow key={ins.installment_id}>
                            <TableCell sx={{ fontWeight: 700 }}>{ins.seq}</TableCell>
                            <TableCell>{formatDate(ins.due_date)}</TableCell>
                            <TableCell>
                              <Chip
                                label={ins.status}
                                size="small"
                                sx={{ fontWeight: 700, borderRadius: "8px", height: 22 }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              ₹{Number(ins.amount_due || 0).toLocaleString("en-IN")}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              ₹{Number(ins.amount_paid || 0).toLocaleString("en-IN")}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </SectionPanel>
              ) : null}

              {Array.isArray(selectedOrder.payments) && selectedOrder.payments.length > 0 ? (
                <SectionPanel>
                  <Typography sx={{ fontWeight: 800, mb: 1, fontSize: 13 }}>
                    Payments
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow
                          sx={{
                            "& .MuiTableCell-root": {
                              fontWeight: 800,
                              fontSize: 11,
                              color: crmColors.text.secondary,
                            },
                          }}
                        >
                          <TableCell>Txn</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Channel</TableCell>
                          <TableCell>Method</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedOrder.payments.map((p) => (
                          <TableRow
                            key={
                              p.transaction_id || `${p.payment_date}-${p.transaction_amount}`
                            }
                          >
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 800, color: crmColors.primary.main }}>
                                {p.transaction_id || "—"}
                              </Typography>
                              {p.gateway_transaction_id ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  PG: {p.gateway_transaction_id}
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell>{formatDate(p.payment_date)}</TableCell>
                            <TableCell>{p.payment_channel || "Manual"}</TableCell>
                            <TableCell>{p.payment_mode_name || "—"}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={p.payment_status_name || p.payment_status || "—"}
                                sx={{
                                  height: 22,
                                  fontWeight: 700,
                                  borderRadius: "8px",
                                  bgcolor: alpha(crmColors.success.main, 0.1),
                                  color: crmColors.success.dark,
                                }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>
                              ₹{Number(p.transaction_amount || 0).toLocaleString("en-IN")}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </SectionPanel>
              ) : (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 2.5,
                    borderRadius: "12px",
                    border: `1px dashed ${crmColors.border.default}`,
                    bgcolor: alpha(crmColors.warning.main, 0.04),
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700, color: crmColors.text.primary }}>
                    No payments recorded yet
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Record a payment to confirm this order
                  </Typography>
                </Box>
              )}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.25,
            py: 1.5,
            borderTop: `1px solid ${crmColors.border.default}`,
            bgcolor: crmColors.background.paper,
          }}
        >
          <Button
            onClick={() => setOrderDialogOpen(false)}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}
          >
            Close
          </Button>
          {selectedOrder && orderPaymentPending(selectedOrder) ? (
            <Button
              variant="contained"
              startIcon={<PaymentIcon />}
              onClick={() => {
                setOrderDialogOpen(false);
                openPayDialog(selectedOrder);
              }}
              sx={{
                textTransform: "none",
                fontWeight: 800,
                borderRadius: "10px",
                bgcolor: crmColors.success.main,
                "&:hover": { bgcolor: crmColors.success.dark },
              }}
            >
              Record payment
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog
        open={payOpen}
        onClose={() => !saving && setPayOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            border: `1px solid ${crmColors.border.default}`,
          },
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            background: `linear-gradient(125deg, ${crmColors.success.dark} 0%, ${crmColors.success.main} 55%, ${crmColors.success.light} 100%)`,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                display: "grid",
                placeItems: "center",
                bgcolor: alpha("#fff", 0.16),
                border: `1px solid ${alpha("#fff", 0.22)}`,
              }}
            >
              <PaymentIcon />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: "1.12rem", lineHeight: 1.2 }}>
                Record payment
              </Typography>
              <Typography sx={{ fontSize: 12, opacity: 0.9, mt: 0.2 }}>
                {selectedOrder?.order_id || ""}
                {selectedOrder
                  ? ` · due ₹${orderDueAmount(selectedOrder).toLocaleString("en-IN")}`
                  : ""}
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            disabled={saving}
            onClick={() => setPayOpen(false)}
            sx={{
              color: "#fff",
              bgcolor: alpha("#fff", 0.12),
              border: `1px solid ${alpha("#fff", 0.18)}`,
              "&:hover": { bgcolor: alpha("#fff", 0.22) },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.75,
            px: { xs: 1.75, sm: 2.25 },
            py: 2,
            bgcolor: alpha(crmColors.success.main, 0.03),
          }}
        >
          {selectedOrder ? (
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                p: 1.25,
                borderRadius: "12px",
                border: `1px solid ${alpha(crmColors.success.main, 0.2)}`,
                bgcolor: crmColors.background.paper,
              }}
            >
              <Chip
                size="small"
                label={selectedOrder.school_name || selectedOrder.school_code || "School"}
                sx={{ fontWeight: 700 }}
              />
              <Chip
                size="small"
                label={`Total ₹${Number(selectedOrder.total_amount || 0).toLocaleString("en-IN")}`}
                variant="outlined"
                sx={{ fontWeight: 700 }}
              />
              <Chip
                size="small"
                label={`Due ₹${orderDueAmount(selectedOrder).toLocaleString("en-IN")}`}
                sx={{
                  fontWeight: 800,
                  bgcolor: alpha(crmColors.warning.main, 0.12),
                  color: crmColors.warning.dark,
                }}
              />
            </Box>
          ) : null}

          <TextField
            select
            label="Payment channel"
            size="small"
            value={payForm.payment_channel}
            onChange={(e) =>
              setPayForm((f) => ({
                ...f,
                payment_channel: e.target.value,
                gateway_transaction_id:
                  e.target.value === "Manual" ? "" : f.gateway_transaction_id,
              }))
            }
            fullWidth
            helperText="Bill Desk = Manual · School online = Payment Gateway"
            InputProps={{ sx: fieldSx }}
          >
            <MenuItem value="Manual">Manual</MenuItem>
            <MenuItem value="Payment Gateway">Payment Gateway</MenuItem>
          </TextField>
          {payForm.payment_channel === "Payment Gateway" ? (
            <TextField
              label="Gateway transaction ID"
              size="small"
              required
              value={payForm.gateway_transaction_id}
              onChange={(e) =>
                setPayForm((f) => ({ ...f, gateway_transaction_id: e.target.value }))
              }
              fullWidth
              InputProps={{ sx: fieldSx }}
            />
          ) : null}
          <TextField
            label="Amount"
            type="number"
            size="small"
            value={payForm.transaction_amount}
            onChange={(e) => setPayForm((f) => ({ ...f, transaction_amount: e.target.value }))}
            fullWidth
            helperText={
              selectedOrder
                ? `Remaining due ₹${orderDueAmount(selectedOrder).toLocaleString("en-IN")}`
                : undefined
            }
            InputProps={{ sx: fieldSx }}
          />
          <TextField
            select
            label="Method"
            size="small"
            value={payForm.payment_method}
            onChange={(e) => setPayForm((f) => ({ ...f, payment_method: e.target.value }))}
            fullWidth
            InputProps={{ sx: fieldSx }}
          >
            {(payMethods.length
              ? payMethods
              : [
                  { payment_mode_id: "Online", payment_mode_name: "Online" },
                  { payment_mode_id: "Transfer", payment_mode_name: "Transfer" },
                  { payment_mode_id: "Offline", payment_mode_name: "Offline" },
                ]
            ).map((m) => (
              <MenuItem key={m.payment_mode_id} value={String(m.payment_mode_id)}>
                {m.payment_mode_name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Payment date"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={payForm.payment_date}
            onChange={(e) => setPayForm((f) => ({ ...f, payment_date: e.target.value }))}
            fullWidth
            InputProps={{ sx: fieldSx }}
          />
          <TextField
            label="Remark"
            size="small"
            value={payForm.payment_remark}
            onChange={(e) => setPayForm((f) => ({ ...f, payment_remark: e.target.value }))}
            fullWidth
            InputProps={{ sx: fieldSx }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.25,
            py: 1.5,
            borderTop: `1px solid ${crmColors.border.default}`,
            bgcolor: crmColors.background.paper,
          }}
        >
          <Button
            onClick={() => setPayOpen(false)}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRecordPayment}
            disabled={saving || !orderPaymentPending(selectedOrder)}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: "10px",
              bgcolor: crmColors.success.main,
              "&:hover": { bgcolor: crmColors.success.dark },
            }}
          >
            {saving ? "Saving…" : "Save payment"}
          </Button>
        </DialogActions>
      </Dialog>

      <MdlAddNewBill
        open={billOpen}
        onClose={() => setBillOpen(false)}
        onCreated={() => fetchOrders()}
        products={rows}
        taxes={taxes}
        payMethods={payMethods}
        initialSchoolCode={billSeed.school}
        initialProductCode={billSeed.product}
      />

    </PageContentLayout>
  );
};

export default BillDesk;
